import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAvatar,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
} from "@miplatform/components";
import { PERMISSIONS, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { AccountType } from "@miplatform/types";
import { isEmpty } from "@miplatform/utils";
import {
  mdiClose,
  mdiFileDocumentOutline,
  mdiHome,
  mdiHomePlus,
} from "@mdi/js";
import {
  AddHomeForm,
  AssignmentCreation,
  Footer,
  HomeAndAccountSearch,
  OrderTemplateSelection,
  useAddHomeForm,
} from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

const PAGES = {
  HOME_SEARCH: "HOME_SEARCH",
  CREATE_HOME: "CREATE_HOME",
  ORDER_TEMPLATE_SELECTION: "ORDER_TEMPLATE_SELECTION",
  CREATE_ASSIGNMENT: "CREATE_ASSIGNMENT",
};

function NoSelectedOrderTemplatePlaceholder() {
  const { classes } = useStyles();

  return (
    <Box className={classes.placeholderContainer}>
      <MiAvatar className={classes.placeholderAvatar}>
        <MiIcon path={mdiFileDocumentOutline} />
      </MiAvatar>
      <Typography className={classes.placeholderText}>
        No Order Selected
      </Typography>
      <Typography className={classes.placeholderSecondaryText}>
        Select an order to get started.
      </Typography>
    </Box>
  );
}

export function CreateNewOrder() {
  const { hasPermission } = usePermissions();

  const [activeScreen, setActiveScreen] = useState(PAGES.HOME_SEARCH);
  const [selectedHome, setSelectedHome] = useState({});
  const [selectedOrderTemplate, setSelectedOrderTemplate] = useState();

  const isCreatingOrder = [
    PAGES.ORDER_TEMPLATE_SELECTION,
    PAGES.CREATE_ASSIGNMENT,
  ].includes(activeScreen);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { classes } = useStyles({ isSmallScreen });
  const { goBack } = useRouter();

  const homeForm = useAddHomeForm({
    hideCreateNewOption: true,
    filterByAccountTypes: [AccountType.Builder],
    onCreate: setSelectedHome,
  });

  const goBackToSearch = () => {
    homeForm.onResetForm();
    setActiveScreen(PAGES.HOME_SEARCH);
    setSelectedOrderTemplate({});
  };

  const handleClose = () => {
    goBackToSearch();
    goBack();
  };

  const handleOrderTemplateSelection = (orderTemplate) => {
    setSelectedOrderTemplate(orderTemplate);
    setActiveScreen(PAGES.CREATE_ASSIGNMENT);
  };

  const handleHomeCreation = async () => {
    const home = await homeForm.handleCreateHome();
    if (home) {
      setActiveScreen(PAGES.ORDER_TEMPLATE_SELECTION);
    }
  };

  return (
    <Box className={classes.root}>
      <MiBuildPageHeader
        title={`Create New ${isCreatingOrder ? "Order" : "Home"}`}
        titleIcon={<MiIcon path={mdiHome} color={theme.palette.primary.main} />}
        actionComponent={
          <MiButton
            startIcon={{
              path: mdiClose,
            }}
            onClick={handleClose}
            style={{ padding: theme.spacing(1, 1) }}
          />
        }
      />
      {activeScreen === PAGES.HOME_SEARCH && (
        <Box className={classes.searchBox}>
          <HomeAndAccountSearch
            hideCreateNewOption={
              !(
                hasPermission(PERMISSIONS.HOME.CREATE) ||
                hasPermission(PERMISSIONS.SYSTEM_ADMINISTRATION.ADMINISTRATION)
              )
            }
            onCreate={() => setActiveScreen(PAGES.CREATE_HOME)}
            onSelect={(option) => {
              setSelectedHome(option);
              setActiveScreen(PAGES.ORDER_TEMPLATE_SELECTION);
            }}
            showLogo
          />
        </Box>
      )}
      {activeScreen === PAGES.CREATE_HOME && (
        <Box className={classes.createHomeContainer}>
          <Box className={classes.createHomeBox}>
            <AddHomeForm
              hidePropertyType
              {...homeForm}
              hideFooter
              onCancel={goBackToSearch}
              onCreate={handleHomeCreation}
            />
            <Box className={classes.propertyCreationBox}>
              <Box
                className={[classes.centerBox, classes.centerPropertyCreation]}
              >
                <Box
                  className={classes.centerBox}
                  width={60}
                  height={60}
                  borderRadius={20}
                  backgroundColor={theme.palette.common.white}
                >
                  <MiIcon path={mdiHomePlus} color={theme.palette.green.dark} />
                </Box>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.BODY1}
                  fontWeight={theme.typography.fontWeightBold}
                >
                  Property Creation
                </Typography>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.CAPTION}
                  color={theme.palette.grey.main}
                >
                  To only save the property address, please click the “Save
                  Button” below.
                </Typography>
              </Box>
            </Box>
          </Box>
          <Footer
            sticky
            show
            saveTitle="Save and Create Order"
            handleSaveClick={handleHomeCreation}
            handleCancelClick={goBackToSearch}
            disableSave={homeForm.isSubmitDisabled}
          />
        </Box>
      )}
      {isCreatingOrder && (
        <Box className={classes.orderCreationContainer}>
          <Box className={classes.orderCreationBox}>
            <OrderTemplateSelection
              home={selectedHome}
              selectedOrderTemplate={selectedOrderTemplate}
              handleOrderTemplateSelection={handleOrderTemplateSelection}
              goBack={goBackToSearch}
            />
          </Box>
          <Box className={classes.orderCreationBox}>
            {isEmpty(selectedOrderTemplate) ? (
              <NoSelectedOrderTemplatePlaceholder />
            ) : (
              <AssignmentCreation
                home={selectedHome}
                selectedOrderTemplate={selectedOrderTemplate}
                onCancel={() => {
                  setActiveScreen(PAGES.ORDER_TEMPLATE_SELECTION);
                  setSelectedOrderTemplate({});
                }}
                onSubmit={handleClose}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme, { isSmallScreen } = {}) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.common.white,
    flex: 1,
    height: "100%",
  },
  searchBox: {
    margin: theme.spacing(40, isSmallScreen ? 20 : 60),
  },
  propertyCreationBox: {
    flex: 1,
    backgroundColor: theme.palette.green.light,
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    maxHeight: "100%",
  },
  createHomeContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    backgroundColor: theme.palette.background.default,
  },
  createHomeBox: {
    display: "flex",
    flexGrow: 1,
    gap: theme.spacing(4),
    margin: theme.spacing(0, 2),
  },
  orderCreationContainer: {
    margin: theme.spacing(4, 2, 0),
    display: "flex",
    overflow: "auto",
    height: "100%",
    gap: theme.spacing(2),
  },
  orderCreationBox: {
    flex: 1,
    overflow: "auto",
  },
  centerBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  centerPropertyCreation: {
    position: "fixed",
    top: "50%",
    gap: theme.spacing(2),
    transform: "translate(0, -25%)",
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: theme.shape.borderRadius,
    gap: theme.spacing(2),
    backgroundColor: theme.palette.green.light,
    height: "99%",
  },
  placeholderAvatar: {
    width: 50,
    height: 50,
    backgroundColor: theme.palette.primary.main,
  },
  placeholderText: {
    fontWeight: theme.fontWeight.medium,
    fontSize: theme.fontSize.xlarge,
  },
  placeholderSecondaryText: {
    fontSize: theme.fontSize.medium,
    color: theme.palette.grey.medium,
  },
}));
